<script>
import moment from 'moment-timezone';
import TalkizyAPI from '@app/services/API/Talkizy';
import talkizyErrorMessage from '@app/utils/talkizyErrors';
import isMeetingFinished, { MEETING_LEAD_MS, formatPlannedDuration } from '@shared/utils/meeting';
import utilsMixin from '@shared/mixins/utils';
import { DEFAULT_MEETING_TIMEZONE } from '@shared/utils/meetingTimezone';
import AddToCalendarDropdown from '@shared/components/AddToCalendarDropdown.vue';

const STATUS_MAP = {
  scheduled: { label: 'Planifiée', type: 'is-success' },
  started: { label: 'En cours', type: 'is-success' },
  ended: { label: 'Terminée', type: 'is-light' },
};

export default {
  name: 'VisioCard',
  components: { AddToCalendarDropdown },
  props: {
    meeting: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      now: Date.now(),
      nowTimer: null,
    };
  },
  mounted() {
    this.nowTimer = setInterval(() => {
      this.now = Date.now();
    }, 30000);
  },
  beforeDestroy() {
    if (this.nowTimer) {
      clearInterval(this.nowTimer);
    }
  },
  computed: {
    isFinished() {
      return isMeetingFinished(this.meeting, this.now);
    },
    // On n'autorise l'annulation que pour une classe à venir : ni terminée,
    // ni en cours (une classe démarrée ou passée ne peut plus être annulée).
    canCancel() {
      return !this.isFinished && this.meeting.status !== 'started';
    },
    // Une classe TERMINEE ne s'annule plus : on propose sa suppression
    // (en remplacement du bouton « Annuler »).
    canDeleteEnded() {
      return this.isFinished;
    },
    canJoin() {
      if (isMeetingFinished(this.meeting, this.now)) return false;
      if (this.meeting.status === 'started') return true;
      if (!this.meeting.starts_at) return false;
      return this.now >= new Date(this.meeting.starts_at).getTime() - MEETING_LEAD_MS;
    },
    status() {
      if (this.isFinished) {
        return { label: 'Terminée', type: 'is-dark' };
      }
      return STATUS_MAP[this.meeting.status] || { label: this.meeting.status, type: 'is-light' };
    },
    createdDay() {
      return this.meeting.created_at ? this.formatDay(this.meeting.created_at) : '';
    },
    // Date + heure dans le fuseau de la classe, formatées comme les événements
    // du calendrier (CalendarEventItem).
    dateLabel() {
      if (!this.meeting.starts_at) return '';
      const tz = this.meeting.timezone || DEFAULT_MEETING_TIMEZONE;
      const start = moment.utc(this.meeting.starts_at).tz(tz);
      return `${start.format('ddd DD MMM YYYY')} · ${start.format('HH:mm')}`;
    },
    timezoneLabel() {
      const tz = this.meeting.timezone || DEFAULT_MEETING_TIMEZONE;
      const offset = moment.tz(this.meeting.starts_at || new Date(), tz).format('Z');
      return `${tz.replace(/_/g, ' ')} (UTC${offset})`;
    },
    durationLabel() {
      // Classe terminée : durée réelle en minutes + secondes, fournie directement
      // par Talkizy (real_duration_seconds), exactement comme la « Durée réelle »
      // des statistiques.
      if (this.isFinished && this.meeting.real_duration_seconds) {
        return utilsMixin.filters.formatSeconds(this.meeting.real_duration_seconds);
      }

      // Sinon : durée planifiée, arrondie à la minute (format homogène "1h 05min").
      if (!this.meeting.ends_at) return null;
      const ms = new Date(this.meeting.ends_at) - new Date(this.meeting.starts_at);
      const minutes = Math.round(ms / 60000);
      return formatPlannedDuration(minutes);
    },
    // participants_count inclut l'animateur ; on l'exclut sans jamais
    // descendre sous 0 (compte à 0 ou absent).
    learnersCount() {
      return Math.max(0, (this.meeting.participants_count || 0) - 1);
    },
    calendarEvent() {
      if (!this.meeting.starts_at) return null;
      const startDate = new Date(this.meeting.starts_at);
      const endDate = this.meeting.ends_at
        ? new Date(this.meeting.ends_at)
        : new Date(startDate.getTime() + 3600000);
      const url = this.meeting.join_url || '';
      return {
        title: this.meeting.title || 'Visio',
        description: url
          ? `Accédez au lien ci-dessous, puis cliquez sur « Rejoindre » pour lancer la visio : ${url}`
          : '',
        location: url,
        startDate,
        endDate,
        url,
      };
    },
    // Le owner voit `creator` sur TOUS les meetings (y compris les siens, où
    // creator = lui-même) : on n'affiche le badge que si le créateur n'est pas
    // l'utilisateur courant, pour ne pas afficher un badge sur ses propres classes.
    // Comparaison par uuid (identité stable) : côté talkizy, User::getFullName()
    // renvoie "Prénom N." (initiale du nom), qui ne peut pas être comparé de
    // façon fiable au nom complet de l'utilisateur courant.
    creatorBadge() {
      const { creator } = this.meeting;
      if (!creator || !creator.name) return null;
      const currentUser = this.$store.getters['auth/user'];
      if (currentUser && creator.uuid && creator.uuid === currentUser.uuid) return null;
      return creator.name;
    },
    // Animateur principal de la classe (« Animé par… »).
    animatorLabel() {
      return this.meeting.main_animator || null;
    },
    // Le viewer peut rejoindre en hôte uniquement si le back lui a fourni SON
    // lien (il est animateur principal ou co-animateur). Un simple créateur
    // (ex. propriétaire ayant programmé la classe pour un admin) n'en a pas.
    canJoinAsHost() {
      return Boolean(this.meeting.join_url);
    },
  },
  methods: {
    goToDetail(event) {
      // Clicks on the actions area (calendar / join / dropdown) must not
      // navigate to the detail page, and must still bubble to document so an
      // already-open dropdown elsewhere closes.
      if (event && event.target.closest && event.target.closest('.media-right')) {
        return;
      }
      this.$router.push({
        name: 'classe_virtuelle_meeting_settings',
        params: { meetingId: this.meeting.meeting_id },
      });
    },
    confirmDelete() {
      this.$buefy.dialog.confirm({
        title: 'Cette action est définitive !',
        message: `
          <div class="content">
            <p>
              <strong>Attention</strong> !
              Vous allez annuler la classe virtuelle
              <strong>${this.meeting.title}</strong>.
            </p>
            <p>Les participants seront notifiés de l'annulation.</p>
            <p class="has-text-danger has-text-weight-bold">
              Cette action est définitive !
            </p>
          </div>
        `,
        type: 'is-danger',
        focusOn: 'cancel',
        onConfirm: () => {
          const loader = this.$buefy.loading.open();
          TalkizyAPI.cancelMeeting(this.meeting.meeting_id)
            .then(() => {
              this.$buefy.toast.open({ message: 'Classe virtuelle annulée.', type: 'is-success' });
              this.$emit('deleted', this.meeting.meeting_id);
            })
            .catch((e) => {
              const message = talkizyErrorMessage(e, 'La suppression a échoué.');
              this.$showMessage.error(message);
              // En cas de timeout, l'annulation a pu aboutir côté Talkizy malgré
              // l'erreur : on demande quand même au parent de recharger la liste
              // pour qu'elle reflète l'état réel.
              this.$emit('deleted', this.meeting.meeting_id);
            })
            .finally(() => loader.close());
        },
      });
    },
    confirmDeleteEnded() {
      this.$buefy.dialog.confirm({
        title: 'Cette action est définitive !',
        message: `
          <div class="content">
            <p>
              <strong>Attention</strong> !
              Vous allez supprimer la classe virtuelle terminée
              <strong>${this.meeting.title}</strong>.
            </p>
            <p>Elle disparaîtra de la liste de vos classes virtuelles.</p>
            <p class="has-text-danger has-text-weight-bold">
              Cette action est définitive !
            </p>
          </div>
        `,
        type: 'is-danger',
        focusOn: 'cancel',
        onConfirm: () => {
          const loader = this.$buefy.loading.open();
          TalkizyAPI.deleteMeeting(this.meeting.meeting_id)
            .then(() => {
              this.$buefy.toast.open({ message: 'Classe virtuelle supprimée.', type: 'is-success' });
              this.$emit('deleted', this.meeting.meeting_id);
            })
            .catch((e) => {
              const message = talkizyErrorMessage(e, 'La suppression a échoué.');
              this.$showMessage.error(message);
              // En cas de timeout, la suppression a pu aboutir côté Talkizy malgré
              // l'erreur : on recharge quand même pour refléter l'état réel.
              this.$emit('deleted', this.meeting.meeting_id);
            })
            .finally(() => loader.close());
        },
      });
    },
    formatDay(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
    },
  },
};
</script>

<template>
  <div class="visiocard box pb-0 overflow-hidden" @click="goToDetail">
    <article class="media">
      <div class="media-content">
        <p class="is-size-8 mb-3">
          <span class="tag mr-3">
            <b-icon class="mr-1 is-size-9" icon="circle" :type="status.type" />
            {{ status.label }}
          </span>
          <span v-if="createdDay">
            <time>Créée le <strong>{{ createdDay }}</strong></time>
          </span>
        </p>

        <h2 class="is-size-5 has-text-weight-bold has-text-black mb-1">
          {{ meeting.title }}
        </h2>

        <p class="is-size-8 has-text-grey-dark">
          <b-icon icon="clock" size="is-small" class="mr-1" />
          {{ dateLabel }}
          <span class="mx-1">·</span>
          <b-icon icon="globe" size="is-small" class="mr-1" />
          {{ timezoneLabel }}
        </p>
      </div>

      <nav class="media-right buttons is-align-self-center">
        <template v-if="!isFinished">
          <AddToCalendarDropdown
            v-if="calendarEvent"
            class="mr-2"
            :calendar-event="calendarEvent"
            append-to-body
          >
            <template #trigger>
              <b-tooltip label="Ajouter au calendrier" position="is-left">
                <b-button icon-left="calendar-plus" />
              </b-tooltip>
            </template>
          </AddToCalendarDropdown>
          <template v-if="canJoinAsHost">
            <a
              v-if="canJoin"
              :href="meeting.join_url"
              target="_blank"
              rel="noopener"
            >
              <b-button type="is-primary">
                Rejoindre
              </b-button>
            </a>
            <b-tooltip
              v-else
              label="Ce bouton sera disponible quelques minutes avant le début de la visio"
              position="is-left"
              multilined
            >
              <b-button type="is-primary" disabled>
                Rejoindre
              </b-button>
            </b-tooltip>
          </template>
        </template>

        <b-dropdown
          class="ml-3"
          position="is-bottom-left"
          aria-role="menu"
          append-to-body
        >
          <template #trigger>
            <b-button type="is-text" icon-left="ellipsis-h" />
          </template>
          <b-dropdown-item has-link aria-role="menuitem">
            <router-link :to="{ name: 'classe_virtuelle_meeting_settings', params: { meetingId: meeting.meeting_id } }">
              Paramètres
            </router-link>
          </b-dropdown-item>
          <b-dropdown-item separator />
          <b-dropdown-item has-link aria-role="menuitem">
            <router-link :to="{ name: 'classe_virtuelle_meeting_learners', params: { meetingId: meeting.meeting_id } }">
              Apprenants
            </router-link>
          </b-dropdown-item>
          <b-dropdown-item has-link aria-role="menuitem">
            <router-link :to="{ name: 'classe_virtuelle_meeting', params: { meetingId: meeting.meeting_id } }">
              Statistiques
            </router-link>
          </b-dropdown-item>
          <template v-if="canCancel">
            <b-dropdown-item separator />
            <b-dropdown-item class="has-text-danger" aria-role="menuitem" @click="confirmDelete">
              Annuler
            </b-dropdown-item>
          </template>
          <template v-else-if="canDeleteEnded">
            <b-dropdown-item separator />
            <b-dropdown-item class="has-text-danger" aria-role="menuitem" @click="confirmDeleteEnded">
              Supprimer
            </b-dropdown-item>
          </template>
        </b-dropdown>
      </nav>
    </article>

    <footer class="-mx-5 mt-5 px-5 py-3 btop-grey">
      <p class="has-text-grey-dark">
        <span v-if="durationLabel" class="is-inline-block is-size-8">
          <b-icon icon="hourglass-half" />
          {{ durationLabel }}
        </span>
        <span class="is-inline-block is-size-8" :class="{ 'ml-4': durationLabel }">
          <b-icon icon="users" />
          {{ learnersCount }} apprenant(s)
        </span>
        <span v-if="animatorLabel" class="is-inline-block is-size-8 ml-4">
          <b-icon icon="chalkboard-teacher" />
          Animée par {{ animatorLabel }}
        </span>
        <span v-if="creatorBadge" class="tag is-info is-light ml-4">
          <b-icon icon="user" size="is-small" class="mr-1" />
          {{ creatorBadge }}
        </span>
      </p>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
.visiocard {
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }
}

</style>
